import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { ArrowLeft, ChevronDown, FileTextIcon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ROLE } from '@/common/role';
import type { WorkPackage } from '@/common/work-package';
import { WorkPackageStatus, workPackageStatusLabel } from '@/common/work-package';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import {
	DELETED_STATUS,
	DeletedStatusLabel,
	deletedOptionClassName,
	deletedTriggerClassName,
} from '@/components/soft-delete/StatusFilterSelect';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { deletedPreviewHref } from '@/hooks/use-deleted-preview';
import { usePermission } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { cn } from '@/lib/utils';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { getListWorkPackagesOptions } from '@/queries/work-packages/list-work-packages';

import { AbsoluteLoader } from '../absoluteLoader';
import { Restricted } from '../Restricted';
import { type SortOption, SortSelect } from '../sort-select';
import { euroInputToCents, GrossTotalFilter, type GrossTotalRange } from './work-packages/GrossTotalFilter';
import { WorkPackageCard } from './work-packages/WorkPackageCard';

// Values must use WORK_PACKAGE_SORT_FIELDS from the backend DTO. There is no status option: the list
// is grouped by status, and each group keeps the chosen order.
export const workPackageSortOptions = (t: TFunction): SortOption[] => [
	{ value: 'createdAt:desc', label: t('common.sort.newest') },
	{ value: 'createdAt:asc', label: t('common.sort.oldest') },
	{ value: 'title:asc', label: t('common.sort.titleAsc') },
	{ value: 'title:desc', label: t('common.sort.titleDesc') },
	{ value: 'grossAmount:desc', label: t('finance.list.sortGrossDesc') },
	{ value: 'grossAmount:asc', label: t('finance.list.sortGrossAsc') },
];
const DEFAULT_SORT = 'createdAt:desc';

export function FinanceList() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { assetId } = useParams<{ assetId: string }>();
	// A work-package status, or "Deleted" for the deleted packages (GH #331)
	const [statusFilter, setStatusFilter] = useState<WorkPackageStatus | 'all' | typeof DELETED_STATUS>('all');
	const [sort, setSort] = useState(DEFAULT_SORT);
	const [grossTotal, setGrossTotal] = useState<GrossTotalRange>({ min: '', max: '' });
	// Deleted packages and restore (GH #331): per page visit, for users who may delete packages
	const canDelete = usePermission(ROLE.WORK_PACKAGES_DELETE);
	const deleted = canDelete && statusFilter === DELETED_STATUS;
	const status = statusFilter !== 'all' && statusFilter !== DELETED_STATUS ? statusFilter : undefined;
	const [packageToRestore, setPackageToRestore] = useState<WorkPackage | null>(null);
	const restorePackage = useRestoreItem({ onRestored: () => setPackageToRestore(null) });
	const packageName = (workPackage: WorkPackage) => workPackage.title || t('common.entities.workPackage', { count: 1 });
	// Kept here, not in each group, so a group stays collapsed when a filter hides it and brings it back.
	const [collapsedStatuses, setCollapsedStatuses] = useState<ReadonlySet<WorkPackageStatus>>(new Set());
	const setGroupOpen = (status: WorkPackageStatus, open: boolean) => {
		setCollapsedStatuses((current) => {
			const next = new Set(current);
			if (open) next.delete(status);
			else next.add(status);
			return next;
		});
	};
	// Wait until the user stops typing before refetching.
	const [debouncedMin] = useDebounceValue(grossTotal.min, 400);
	const [debouncedMax] = useDebounceValue(grossTotal.max, 400);
	const minGrossAmount = euroInputToCents(debouncedMin);
	const maxGrossAmount = euroInputToCents(debouncedMax);
	const grossTotalError =
		minGrossAmount === null || maxGrossAmount === null
			? t('finance.list.grossTotalNegative')
			: minGrossAmount !== undefined && maxGrossAmount !== undefined && minGrossAmount > maxGrossAmount
				? t('finance.list.grossTotalInvalid')
				: undefined;
	const {
		data: workPackagesResponse,
		isLoading,
		isPlaceholderData,
	} = useQuery({
		...getListWorkPackagesOptions(
			{
				assetId: assetId as string,
				limit: 100,
				offset: 0,
				status,
				minGrossAmount: minGrossAmount ?? undefined,
				maxGrossAmount: maxGrossAmount ?? undefined,
				sort,
				...(deleted && { deleted: true }),
			},
			// An invalid range isn't sent (the API would reject it); the last valid result stays on screen.
			!!assetId && !grossTotalError,
		),
		// Keep the current cards on screen while a new sort, filter or an invalid range is pending.
		placeholderData: keepPreviousData,
	});
	const workPackages = workPackagesResponse?.workPackages || [];
	// The list loads one page and has no pager yet, so say when it isn't everything.
	const total = workPackagesResponse?.total ?? 0;
	// Group in workflow order. Filtering keeps the API's order, so each group stays sorted.
	const statusGroups = Object.values(WorkPackageStatus)
		.map((status) => ({ status, items: workPackages.filter((workpackage) => workpackage.status === status) }))
		.filter((group) => group.items.length > 0);
	const isFiltered = !!status || minGrossAmount !== undefined || maxGrossAmount !== undefined;

	const onCardClick = (workpackageId: string) => {
		const path = `/assets/${assetId}/finance/work-packages/${workpackageId}`;
		// A deleted package opens as a read-only preview (GH #331)
		navigate(deleted ? deletedPreviewHref(path, 'workPackage') : path);
	};
	const onCreateNew = () => {
		navigate(`work-packages/new`);
	};
	return (
		<div className="h-full flex flex-col">
			<div className="px-6 pt-6 pb-4">
				<Button
					variant="ghost"
					className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! mb-2 gap-0 hover:pl-2! cursor-pointer"
					onClick={() => navigate(-1)}
				>
					<ArrowLeft className="size-4 mr-1!" />
					{t('common.actions.back')}
				</Button>
				<div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3 mb-6">
					<h1 className="text-foreground text-2xl font-medium">{t('finance.list.title')}</h1>
					{/* The taller row gap leaves room for the gross total error when the toolbar wraps. */}
					<div className="flex flex-wrap items-center gap-x-3 gap-y-6">
						<div className="flex items-center gap-2">
							<span className="text-xs lg:text-sm font-medium text-muted-foreground">{t('common.labels.status')}:</span>
							<Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
								<SelectTrigger
									aria-label={t('finance.list.statusFilterAria')}
									className={cn(
										'bg-input w-36 lg:w-45 h-8 lg:min-h-10 text-xs lg:text-sm',
										deleted && deletedTriggerClassName,
									)}
								>
									<SelectValue placeholder={workPackageStatusLabel(t, WorkPackageStatus.Pending)} />
								</SelectTrigger>
								<SelectContent position="popper">
									<SelectItem value="all">{t('common.states.all')}</SelectItem>
									{Object.values(WorkPackageStatus).map((status) => (
										<SelectItem key={status} value={status}>
											{workPackageStatusLabel(t, status)}
										</SelectItem>
									))}
									{canDelete && (
										<>
											<SelectSeparator />
											<SelectItem value={DELETED_STATUS} className={deletedOptionClassName}>
												<DeletedStatusLabel />
											</SelectItem>
										</>
									)}
								</SelectContent>
							</Select>
						</div>
						<GrossTotalFilter value={grossTotal} onChange={setGrossTotal} error={grossTotalError} />
						<SortSelect options={workPackageSortOptions(t)} value={sort} onChange={setSort} />
						<Restricted to={ROLE.WORK_PACKAGES_CREATE}>
							<Button size="sm" className="h-8 lg:h-10 text-xs lg:text-sm px-2 lg:px-4" onClick={onCreateNew}>
								<Plus className="size-3.5 lg:size-4" />
								{t('finance.list.createNew')}
							</Button>
						</Restricted>
					</div>
				</div>
			</div>
			<div className="flex-1 overflow-auto px-6 pb-6 space-y-6 relative">
				{deleted && <DeletedViewBanner />}
				{total > workPackages.length && (
					<p className="text-sm text-muted-foreground">
						{t('finance.list.showingOf', { shown: workPackages.length, total })}
					</p>
				)}
				{statusGroups.map(({ status, items }) => {
					const open = !collapsedStatuses.has(status);
					return (
						<Collapsible key={status} open={open} onOpenChange={(next) => setGroupOpen(status, next)} asChild>
							<section aria-labelledby={`work-packages-${status}`} className="space-y-3">
								<h2 id={`work-packages-${status}`} className="text-foreground text-lg font-medium">
									<CollapsibleTrigger className="flex items-center gap-2 cursor-pointer hover:text-primary">
										<ChevronDown className={cn('size-4 transition-transform', !open && '-rotate-90')} />
										{workPackageStatusLabel(t, status)}{' '}
										<span className="text-muted-foreground text-sm font-normal">({items.length})</span>
									</CollapsibleTrigger>
								</h2>
								<CollapsibleContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									{items.map((workpackage) => (
										<WorkPackageCard
											key={workpackage.id}
											workpackage={workpackage}
											onCardClick={onCardClick}
											onRestore={deleted ? setPackageToRestore : undefined}
										/>
									))}
								</CollapsibleContent>
							</section>
						</Collapsible>
					);
				})}
				{!isLoading && !workPackages.length && isFiltered && (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<FileTextIcon className="h-16 w-16 text-muted-foreground mb-4" />
						<h3 className="text-foreground mb-2">{t('finance.list.noMatchesTitle')}</h3>
						<p className="text-muted-foreground max-w-sm">{t('finance.list.noMatchesHint')}</p>
					</div>
				)}
				{!isLoading && !workPackages.length && deleted && !isFiltered && (
					<p className="text-center text-muted-foreground py-10">{t('common.deleted.empty')}</p>
				)}
				{!isLoading && !workPackages.length && !isFiltered && !deleted && (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<FileTextIcon className="h-16 w-16 text-muted-foreground mb-4" />
						<h3 className="text-foreground mb-2">{t('finance.list.emptyTitle')}</h3>
						<p className="text-muted-foreground mb-4 max-w-sm">{t('finance.list.emptyDescription')}</p>
						<Restricted to={ROLE.WORK_PACKAGES_CREATE}>
							<Button onClick={onCreateNew} className="bg-blue-600 hover:bg-blue-700 text-white">
								<Plus className="h-4 w-4 mr-2" />
								{t('finance.list.createNew')}
							</Button>
						</Restricted>
					</div>
				)}
				{packageToRestore && (
					<RestoreConfirmDialog
						open
						onOpenChange={(open) => !open && setPackageToRestore(null)}
						target={softDeleteTargets.workPackage(packageToRestore.id as string)}
						name={packageName(packageToRestore)}
						isRestoring={restorePackage.isPending}
						onConfirm={() =>
							restorePackage.mutate({
								target: softDeleteTargets.workPackage(packageToRestore.id as string),
								name: packageName(packageToRestore),
							})
						}
					/>
				)}
				{/* Also while a new sort or filter loads over the previous cards (not while an invalid range just keeps them). */}
				<AbsoluteLoader
					className={cn(isLoading && 'bg-transparent')}
					isLoading={isLoading || (isPlaceholderData && !grossTotalError)}
				/>
			</div>
		</div>
	);
}
